import React from 'react';
import striptags from 'striptags';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import Styled from 'styled';
import ImageView from 'lib/images/responsive-image-view';
import map from 'lib/react/html/map';
import RouteLinkView from 'lib/ui/route-link-view';
import Helmet from 'helmet/helmet';
import { RetryDialog } from 'lib/alerts/dialog';
import withDialog from 'lib/alerts/with-dialog';
import { InterscrollerPlaceholder } from 'lib/google/dfp/ad-units/interscroller';
import { ForumRectangle, ZoznamBoxin } from 'lib/google/dfp/ad-units/forum-rectangle';
import ScrollToTop from 'lib/scroll/scroll-to-top';
import loadScript from 'lib/script/script';

import gaTrack from 'four-nets/lib/google-analytics/track';
import { NotFoundError } from 'four-nets/errors/errors';
import Attachment from 'four-nets/mails/attachment/attachment';
import LoadingView from 'four-nets/ui/loading-view';
import TitleView from 'four-nets/ui/title-view';
import GalleryView from 'four-nets/ui/gallery/gallery-view';
import { Site } from 'four-nets/site';
import Newsletter from 'four-nets/newsletter/newsletter';
import ForumRelatedDiscussionsLoader from './forum-related-discussions/forum-related-discussions-loader';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';

import FacebookButtonView from 'four-nets/ui/facebook-button/facebook-button-view';
import TwitterButtonView from 'four-nets/ui/twitter-button/twitter-button-view';
import LikeButtonView from 'four-nets/ui/like-button-view';

import VendorWidget from './widgets/vendor-widget/vendor-widget';
import BlogPostStatusBarView, { BlogPostStatusBarViewModel } from './blog-post-status-bar/blog-post-status-bar-view';
import defaultStyles from './blog-post-detail-view.scss';
import { toGlobalId } from 'graphql-relay';
import BazaarWidget from './widgets/bazaar-widget/bazaar-widget';
import ForumWidget from './widgets/forum-widget/forum-widget';
import InspirationsWidget from './widgets/inspirations-widget/inspirations-widget';
import BlogAlbumWidget from './widgets/blog-album-widget/blog-album-widget';

const WP_IMAGE_REGEX = /^wp-image-(\d+)$/;
const WP_ATTACHMENT_REGEX = /^attachment_(\d+)$/;
const GALLERY_REGEX = /gallery-view/;
const ATTACHMENTS_REGEX = /attachments-view/;
const INTERSCROLLER_REGEX = /interscroller/;

@Styled(defaultStyles)
@observer
class BlogPostContentView extends React.Component {
    static scriptID = 0;
    static loadInstagram = false;

    findId(selector = '', regex) {
        const split = selector.split(' ');

        for (let i = 0; i < split.length; i++) {
            const item = split[i];
            const match = item.match(regex);

            if (match) {
                const number = parseInt(match[1], 10);
                if (!isNaN(number)) {
                    return number;
                }
            }
        }

        return null;
    }

    onElement = (name, props, children) => {
        const post = this.props.post;
        const styles = this.props.styles;

        switch (name) {
            case 'script': {
                if (props.src && props.src.indexOf('instagram') !== -1) {
                    BlogPostContentView.loadInstagram = true;
                    return null;
                }
                return (
                    <script
                        id={`wp-script-${BlogPostContentView.scriptID++}`}
                        dangerouslySetInnerHTML={{ __html: React.Children.toArray(children).join(' ') }}
                    />
                );
            }
            case 'p': {
                // <p> containing only br, newline and img with style display none contains
                // just the pixel impressions tracking image and we want to hide it
                // It's not enough to hide just the image, because <p>s are styled with margin
                // so empty <p> (with image display: none in it) still takes space, so we set
                // display: none on the <p> itself
                if (
                    children &&
                    children.length === 3 &&
                    children[0].type === 'br' &&
                    children[1] === '\n' &&
                    children[2].type === 'img' &&
                    children[2].props &&
                    children[2].props.style &&
                    children[2].props.style.display &&
                    children[2].props.style.display.match(/^\s*none\s*$/)
                ) {
                    return (
                        <p {...props} style={{ display: 'none' }}>
                            {children}
                        </p>
                    );
                }

                if (
                    children &&
                    children.length === 1 &&
                    children[0].match &&
                    children[0].match(/^\[(forum|bazaar|vendors|inspirations) ([\d ,]+)]/)
                ) {
                    const result = children[0].match(/^\[(forum|bazaar|vendors|inspirations) ([\d ,]+)]/);

                    const [, type, ids] = result;

                    switch (type) {
                        case 'forum': {
                            const categoryIds = ids
                                .split(' ')
                                .map(_id => parseInt(_id, 10))
                                .filter(e => !Number.isNaN(e))
                                .map(_id => toGlobalId('ThreadCategoryNode', _id));
                            return <ForumWidget categoryIds={categoryIds} />;
                        }
                        case 'bazaar': {
                            const categoryIds = ids
                                .split(' ')
                                .map(_id => parseInt(_id, 10))
                                .map(_id => toGlobalId('ServerCategoryNode', _id));
                            return <BazaarWidget categoryIds={categoryIds} />;
                        }
                        case 'vendors': {
                            const categoryIds = ids
                                .split(' ')
                                .map(_id => parseInt(_id, 10))
                                .map(_id => toGlobalId('CatalogCategoryNode', _id));
                            return <VendorWidget categoryIds={categoryIds} />;
                        }
                        case 'inspirations': {
                            const tagIds = ids
                                .split(' ')
                                .map(_id => parseInt(_id, 10))
                                .map(_id => toGlobalId('TagNode', _id));
                            return <InspirationsWidget tagIds={tagIds} />;
                        }
                        default:
                            return null;
                    }
                }

                if (
                    children &&
                    children.length === 1 &&
                    children[0].match &&
                    children[0].match(/^\[blog-album ([^ ]+) (.+)\]/)
                ) {
                    const result = children[0].match(
                        /^\[blog-album \/blog\/(?<username>[^ \/]+)\/album\/(?<slug>[^ \/]+)\/? (?<label>.+)\]/
                    );
                    if (result) {
                        const { username, slug, label } = result.groups;
                        return <BlogAlbumWidget slug={slug} username={username} label={label} />;
                    }
                    return null;
                }

                return undefined;
            }
            case 'img': {
                // If image is styled with "display: none;", don't touch it.
                // It's used for pixel impressions tracking. This has to be here
                // for previous case 'p' branch to match.
                if (props && props.style && props.style.display && props.style.display.match(/^\s*none\s*$/)) {
                    return undefined;
                }
                const id = this.findId(props.className, WP_IMAGE_REGEX);

                const image = post.attachment[id];

                if (!image) {
                    const { width, height, ...rest } = props;

                    return <ImageView {...rest} linkify={false} aspectFill={true} />;
                }

                const {
                    mediaDetails: { height, width },
                } = image;
                const aspectRatio = height / width;

                const classNames = [styles.image];

                if (aspectRatio >= 0.9 && props.className.indexOf('size-full') === -1) {
                    classNames.push(styles.portrait);
                }

                return (
                    <span key={id} className={classNames.join(' ')}>
                        <ImageView
                            alt={props.alt}
                            linkify={false}
                            image={image}
                            aspectRatio={aspectRatio}
                            sizes={this.props.sizes}
                        />
                    </span>
                );
            }
            case 'a': {
                const classNames = [styles.link];
                if (props.className) {
                    classNames.push(props.className);
                }

                let onClick;
                if (props.className === 'read-more__link' && typeof window !== 'undefined') {
                    onClick = () => {
                        gaTrack('event', 'Read More Link', 'click', 'Read More Old Magazine Link');
                    };
                }

                return (
                    <RouteLinkView
                        key={children}
                        {...props}
                        className={classNames.join(' ')}
                        onClick={onClick}
                        follow={true}
                    >
                        {children}
                    </RouteLinkView>
                );
            }
            case 'figure': {
                const id = this.findId(props.id, WP_ATTACHMENT_REGEX);
                const image = post.attachment[id];

                if (!image) {
                    return undefined;
                }

                const {
                    mediaDetails: { height, width },
                } = image;
                const aspectRatio = height / width;

                const classNames = [styles.figure];

                if (aspectRatio >= 0.9) {
                    classNames.push(styles.portrait);
                }

                return (
                    <figure key={props.id} className={classNames.join(' ')}>
                        {children}
                    </figure>
                );
            }

            default:
                return undefined;
        }
    };

    onComment = data => {
        const post = this.props.post;
        const styles = this.props.styles;
        const site = this.props.site;

        if (data.match(GALLERY_REGEX) && post.gallery.length) {
            return (
                <div key='gallery' className={styles.gallery}>
                    <GalleryView
                        altPrefix={`${post.title.rendered} - `}
                        limit={8}
                        items={post.gallery}
                        align={'center'}
                    />
                </div>
            );
        }
        if (data.match(ATTACHMENTS_REGEX) && post.attachments.length) {
            return (
                <div key='attachments' className={styles.attachments}>
                    {post.attachments.map(attachment => (
                        <div key={attachment.id} className={styles.attachmentsWrapper}>
                            <Attachment
                                attachment={{
                                    downloadUrl: attachment.sourceUrl,
                                    originalFilename: attachment.title.rendered,
                                }}
                            />
                        </div>
                    ))}
                </div>
            );
        }
        if (data.match(INTERSCROLLER_REGEX)) {
            return (
                <>
                    {site.isWedding && site.isSlovak ? <ZoznamBoxin position='top' /> : <ForumRectangle />}
                    <InterscrollerPlaceholder />
                </>
            );
        }
    };

    componentDidMount() {
        if (BlogPostContentView.loadInstagram) {
            loadScript('https://www.instagram.com/embed.js');
        }
    }

    render() {
        const { post, styles } = this.props;

        return (
            <div className={styles.postDetail}>{map(post.content.rendered, this.onElement, null, this.onComment)}</div>
        );
    }
}

export class BlogPostDetailViewModel {
    post: Post;

    constructor(dataSource, slug) {
        this.slug = slug;
        this.dataSource = dataSource;
    }

    loadDidSucceed = post => {
        this.post = post;

        this.blogPostStatusBarViewModel = new BlogPostStatusBarViewModel({
            post,
        });
    };

    load = () => {
        const media = ['id', 'source_url', 'title', 'alt_text', 'media_details', 'caption', 'guid'];

        return this.dataSource.postStore
            .loadAll({
                query: {
                    slug: this.slug,
                    per_page: 1,
                    fields: JSON.stringify([
                        'date_gmt',
                        'read_count',
                        'displayed_author',
                        'photo_source',
                        'photo_source_url',
                        ['attachments', media],
                        ['featured_media', media],
                        'seo',
                        'slug',
                        'comments_count',
                        'excerpt',
                        'liker_ids',
                        'title',
                        'content',
                        ['categories', ['id', 'name', 'slug']],
                        'date',
                        ['gallery', media],
                        'attached_media',
                    ]),
                },
            })
            .then(result => {
                let post;

                for (let i = 0; i < result.results.length; i++) {
                    if (result.results[i].slug === this.slug) {
                        post = result.results[i];
                        break;
                    }
                }

                if (!post) {
                    return Promise.reject(new NotFoundError());
                }

                return this.loadDidSucceed(post);
            });
    };

    fromJSON(json) {
        this.loadDidSucceed(this.dataSource.postStore.processJSON({ json: json.post }));
    }

    toJSON() {
        return {
            post: this.post,
        };
    }
}

@withDialog
@Styled(defaultStyles)
@observer
class BlogPostDetailView extends React.Component {
    static contextTypes = {
        authCheck: PropTypes.func,
        origin: PropTypes.string,
        site: PropTypes.instanceOf(Site),
    };

    like = () => {
        const { post } = this.props.blogPostDetailViewModel;

        this.context.authCheck(post.toggleLike.bind(post)).catch(err => {
            this.props.showDialog(
                new RetryDialog({
                    err: err.message,
                    retry: this.like,
                })
            );
            return Promise.resolve();
        }, AuthOrigin.LikeBlogPost);
    };

    componentDidMount() {
        if (this.context.site && this.context.site.isWedding && this.context.site.isSlovak) {
            loadScript('https://assets.strossle.com/strossle-widget-sdk/1/strossle-widget-sdk.js').then(() => {
                const script = document.createElement('script');
                script.innerHTML = "strossle('ad0904ca-9a95-44a2-bfce-41b9d6a6ba32', '.strossle-widget-footer')";
                document.body.appendChild(script);
            });
        }

        for (let i = 0; i < BlogPostContentView.scriptID; i++) {
            const wpScript = document.getElementById(`wp-script-${i}`);
            if (wpScript) {
                eval(wpScript.innerHTML);
            }
        }

        this.props.blogPostDetailViewModel.post.incrementReadCount();
    }

    renderHelmet = H => {
        const { site, origin } = this.context;
        const { post } = this.props.blogPostDetailViewModel;

        const title = `${post.title.rendered} | ${site.name}`;
        const description = striptags(post.excerpt.rendered);

        const data = [
            <title>{title}</title>,
            <meta property='description' content={description} />,
            <meta property='og:description' content={description} />,
            <meta property='og:title' content={title} />,
            <meta property='og:image' content={post.featuredMedia.sourceUrl} />,
            <meta property='og:image:width' content={post.featuredMedia.mediaDetails.width} />,
            <meta property='og:image:height' content={post.featuredMedia.mediaDetails.height} />,
            <meta property='og:url' content={`${origin}/magazine/${post.slug}`} />,
            <meta property='og:type' content='article' />,
        ];

        if (post.scriptCode) {
            const scripts = [];

            map(post.scriptCode).forEach(element => {
                if (element.type === 'script') {
                    const { children } = element.props;
                    if (children) {
                        scripts.push(
                            React.cloneElement(element, undefined, React.Children.toArray(children).join(' '))
                        );
                    } else {
                        scripts.push(element);
                    }
                }
            });

            if (scripts.length) {
                data.push(...scripts);
            }
        }

        return <H>{React.Children.toArray(data)}</H>;
    };

    render() {
        const { blogPostDetailViewModel, url, styles } = this.props;
        const { site } = this.context;
        const { post, blogPostStatusBarViewModel } = blogPostDetailViewModel;

        return (
            <>
                <div className={styles.postContent}>
                    <ScrollToTop post={post} />
                    <Helmet render={this.renderHelmet} />
                    <TitleView title={<h1 className={styles.heading}>{post.title.rendered}</h1>} />
                    <BlogPostStatusBarView blogPostStatusBarViewModel={blogPostStatusBarViewModel} />
                    <div className={styles.contentWrapper}>
                        <BlogPostContentView
                            post={post}
                            site={site}
                            sizes={{
                                xs: '100vw',
                                lg: 730,
                            }}
                        />
                        {site.isGarden ? <Newsletter /> : null}
                        <ForumRelatedDiscussionsLoader
                            query={
                                (post &&
                                    post.seo &&
                                    post.seo.aioseopKeywords &&
                                    post.seo.aioseopKeywords.length &&
                                    post.seo.aioseopKeywords[0]) ||
                                post.slug
                            }
                        />
                    </div>
                    <div className={styles.social}>
                        {[
                            <LikeButtonView
                                onClick={this.like}
                                liked={post.isLikedByMe}
                                count={post.likesCount || null}
                            />,
                            <FacebookButtonView />,
                            <TwitterButtonView
                                shareOptions={{
                                    url,
                                }}
                            />,
                        ].map((element, index) => (
                            <span
                                key={index} // eslint-disable-line
                                className={styles.socialItem}
                            >
                                {element}
                            </span>
                        ))}
                    </div>
                    <LoadingView>{this.props.children}</LoadingView>
                </div>
                <div className='strossle-widget-footer' />
            </>
        );
    }
}

export default BlogPostDetailView;
